import {RealtimePostgresInsertPayload} from '@supabase/supabase-js';
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Id, toast} from 'react-toastify';
import {useNotificationCenter} from 'react-toastify/addons/use-notification-center';

import {AnyIconNameT} from '@/lib/types/icons/libraries.types';
import {NotificationInsertT, NotificationT, ToastOptionsT, ToastPropsT} from '@/lib/types/notifications';

import {SupabaseInstance} from '@/lib/utils';

import {
  ErrorToast,
  InfoToast,
  SuccessToast,
  WarningToast,
} from '@/pages/speculators/notifications/components/CustomToast';

import {useDataQuery} from '@/hooks/useDataQuery';

import {useAuth} from '@/providers/components/authProvider';

type NotificationsContextT = {
  // Properties
  notifications: NotificationT[];
  archivedNotifications: NotificationT[];
  unreadCount: number;
  loading: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error';
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    setPage: (page: number) => void;
  };
  archivedPagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    setPage: (page: number) => void;
  };
  // Search
  searchQuery: string;
  searchResults: NotificationT[];
  isSearching: boolean;
  setSearchQuery: (query: string) => void;
  // Functions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAllAsUnread: () => Promise<void>;
  archiveNotification: (notificationId: string) => Promise<void>;
  unarchiveNotification: (notificationId: string) => Promise<void>;
  archiveAllNotifications: () => Promise<void>;
  unarchiveAllNotifications: () => Promise<void>;
  removeNotification: (notificationId: string) => Promise<void>;
  createNotification: (data: NotificationInsertT) => Promise<NotificationT>;
  resetConnection: () => Promise<void>;
  searchNotifications: (query: string) => Promise<NotificationT[]>;
  toast: {
    info: (props: ToastPropsT | string, options?: Partial<ToastOptionsT>) => Id;
    success: (props: ToastPropsT | string, options?: Partial<ToastOptionsT>) => Id;
    warning: (props: ToastPropsT | string, options?: Partial<ToastOptionsT>) => Id;
    error: (props: ToastPropsT | string, options?: Partial<ToastOptionsT>) => Id;
    dismiss: (toastId?: Id) => void;
  };
  // Debug properties
  debugEvents?: {
    onNotificationEvent?: (notification: NotificationT, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void;
    onNotificationError?: (error: Error) => void;
  };
};

// Create the context
const NotificationsContext = createContext<NotificationsContextT | null>(null);
NotificationsContext.displayName = 'NotificationsProvider';

// Default toast options
const ITEMS_PER_PAGE = 10;
const defaultToastOptions: ToastOptionsT = {
  autoClose: 5000,
  autoCloseTimeout: 5000,
  theme: 'dark',
  icon: false,
  closeButton: false,
  hideProgressBar: false,
  pauseOnHover: true,
  closeOnClick: true,
  draggable: true,
};

// Helper function to create toast options
const createToastOptions = (notification: NotificationT): ToastOptionsT => {
  // If auto_close is false, set autoClose to false (manual dismiss)
  // If auto_close is true, use the timeout value
  const autoClose =
    notification.auto_close === false ? false : (notification.auto_close_timeout ?? defaultToastOptions.autoClose);
  const hideProgressBar = notification.hide_progress_bar ?? defaultToastOptions.hideProgressBar;
  const autoCloseTimeout = notification.auto_close_timeout ?? defaultToastOptions.autoCloseTimeout;
  return {
    ...defaultToastOptions,
    autoClose,
    hideProgressBar,
    autoCloseTimeout,
  };
};

// Helper function to convert notification to toast props
const createToastProps = (props: NotificationT): ToastPropsT => {
  return {
    title: props.title,
    message: props.message,
    icon: props.icon ? (props.icon as AnyIconNameT) : undefined,
    link: props.link ?? undefined,
    linkText: props.link_text ?? undefined,
    openInNewTab: props.open_in_new_tab ?? undefined,
  };
};

// Create the provider component
export const NotificationsProvider = ({children}: {children: React.ReactNode}) => {
  const {markAllAsRead: markAllToastsAsRead} = useNotificationCenter();
  const {isAuthenticated, authenticating} = useAuth();

  // State
  const [activePage, setActivePage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NotificationT[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>(
    'connecting'
  );

  // Refs
  const channelInitializingRef = useRef(false);
  const channelRef = useRef<ReturnType<typeof SupabaseInstance.supabase.channel> | null>(null);
  const debugEventsRef = useRef<{
    onNotificationEvent?: (notification: NotificationT, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void;
    onNotificationError?: (error: Error) => void;
  }>({});

  // Get active notifications
  const {
    data: activeNotificationsData,
    refetch: refetchActive,
    isLoading: isLoadingActive,
  } = useDataQuery({
    table: 'notification',
    page: activePage,
    limit: ITEMS_PER_PAGE,
    filters: [
      {
        column: 'status',
        operator: 'neq',
        value: 'archived',
      },
    ],
    sortOption: {
      column: 'created_at',
      direction: 'desc',
      label: 'Created At',
    },
    enabled: !authenticating && isAuthenticated,
  });

  // Get archived notifications
  const {
    data: archivedNotificationsData,
    refetch: refetchArchived,
    isLoading: isLoadingArchived,
  } = useDataQuery({
    table: 'notification',
    page: archivedPage,
    limit: ITEMS_PER_PAGE,
    filters: [
      {
        column: 'status',
        operator: 'eq',
        value: 'archived',
      },
    ],
    sortOption: {
      column: 'created_at',
      direction: 'desc',
      label: 'Created At',
    },
    enabled: !authenticating && isAuthenticated,
  });

  // Computed
  const notifications = useMemo(() => activeNotificationsData?.data ?? [], [activeNotificationsData?.data]);
  const archivedNotifications = useMemo(() => archivedNotificationsData?.data ?? [], [archivedNotificationsData?.data]);
  const {
    currentPage: activeCurrentPage = 1,
    totalPages: activeTotalPages = 1,
    total: activeTotalItems = 0,
  } = activeNotificationsData ?? {};
  const {
    currentPage: archivedCurrentPage = 1,
    totalPages: archivedTotalPages = 1,
    total: archivedTotalItems = 0,
  } = archivedNotificationsData ?? {};
  const unreadCount = useMemo(() => notifications.filter(n => n.status === 'unread').length, [notifications]);

  // Refetch both lists when needed
  const refetchAll = useCallback(() => {
    refetchActive();
    refetchArchived();
  }, [refetchActive, refetchArchived]);

  // Toast functions
  const toastFn = useMemo(
    () => ({
      info: (props: ToastPropsT | string, customOptions?: Partial<ToastOptionsT>) => {
        const options = {
          ...defaultToastOptions,
          ...customOptions,
        };
        // Handle string input by converting to ToastPropsT with default title
        const toastProps = typeof props === 'string' ? {title: 'Information', message: props} : props;
        return toast.info(<InfoToast {...toastProps} />, options);
      },
      success: (props: ToastPropsT | string, customOptions?: Partial<ToastOptionsT>) => {
        const options = {
          ...defaultToastOptions,
          ...customOptions,
        };
        // Handle string input by converting to ToastPropsT with default title
        const toastProps = typeof props === 'string' ? {title: 'Success', message: props} : props;
        return toast.success(<SuccessToast {...toastProps} />, options);
      },
      warning: (props: ToastPropsT | string, customOptions?: Partial<ToastOptionsT>) => {
        const options = {
          ...defaultToastOptions,
          ...customOptions,
        };
        // Handle string input by converting to ToastPropsT with default title
        const toastProps = typeof props === 'string' ? {title: 'Warning', message: props} : props;
        return toast.warning(<WarningToast {...toastProps} />, options);
      },
      error: (props: ToastPropsT | string, customOptions?: Partial<ToastOptionsT>) => {
        const options = {
          ...defaultToastOptions,
          ...customOptions,
        };
        // Handle string input by converting to ToastPropsT with default title
        const toastProps = typeof props === 'string' ? {title: 'Error', message: props} : props;
        return toast.error(<ErrorToast {...toastProps} />, options);
      },
      dismiss: (toastId?: Id) => toast.dismiss(toastId),
    }),
    []
  );

  // Notification Functions
  const onNotificationInsert = useCallback(
    (payload: RealtimePostgresInsertPayload<NotificationT>) => {
      // Get the new notification from the payload
      const {new: notification} = payload;
      if (!notification) return;

      // Display the toast
      const type = notification.type ? notification.type : 'info';
      const props = createToastProps(notification);
      const options = createToastOptions(notification);
      toastFn[type](props, options);

      // Notify debug components
      debugEventsRef.current.onNotificationEvent?.(notification, 'INSERT');

      // Refetch tanstack query
      refetchAll();
    },
    [refetchAll, toastFn]
  );

  // Search notifications using full-text search
  const searchNotifications = useCallback(async (query: string): Promise<NotificationT[]> => {
    setIsSearching(true);
    try {
      const {data, error} = await SupabaseInstance.supabase
        .from('notification')
        .select('*')
        .textSearch('title || message', query, {
          type: 'websearch',
          config: 'english',
        })
        .order('created_at', {ascending: false})
        .limit(20);

      if (error) {
        console.error('Error searching notifications:', error);
        return [];
      }

      setSearchResults(data);
      return data;
    } catch (error) {
      console.error('Error searching notifications:', error);
      return [];
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search query changes
  useEffect(() => {
    if (searchQuery.length === 0) {
      setSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      searchNotifications(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, searchNotifications]);

  // Functions
  const createNotification = useCallback(async (data: NotificationInsertT) => {
    const user = await SupabaseInstance.getCurrentUser();
    if (!user) throw new Error('No user found');

    const session = await SupabaseInstance.supabase.auth.getSession();
    if (!session.data.session) throw new Error('No active session');

    try {
      const response = await SupabaseInstance.supabase.functions.invoke('notifications', {
        method: 'POST',
        body: data,
      });

      // Check for error response
      if (response.error) {
        // Parse the error response
        const errorData = response.error;
        let errorMessage = 'Failed to create notification';

        // Check if it's a validation error with details
        if (errorData.message === 'Validation failed' && errorData.context?.errors) {
          // Type assertion for validation errors
          const validationErrors = errorData.context.errors as Record<string, string[]>;

          // Format validation errors
          const errors = Object.entries(validationErrors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          errorMessage = `Validation Error:\n${errors}`;
        } else if (errorData.message) {
          // Use the error message from the response
          errorMessage = errorData.message;
        }

        throw new Error(errorMessage);
      }

      return response.data;
    } catch (error) {
      console.error('Error creating notification:', error);
      // Notify debug components about the error
      debugEventsRef.current.onNotificationError?.(
        error instanceof Error ? error : new Error('Failed to create notification')
      );
      toast.error(error instanceof Error ? error.message : 'Failed to create notification', defaultToastOptions);
      throw error;
    }
  }, []);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      const {error} = await SupabaseInstance.supabase.rpc('mark_notification_as_read', {
        notification_id_param: notificationId,
      });

      if (error) {
        console.error('Error marking notification as read:', error);
        toast.error('Failed to mark notification as read', defaultToastOptions);
        return;
      }

      refetchAll();
    },
    [refetchAll]
  );

  const markAllAsRead = useCallback(async () => {
    const user = await SupabaseInstance.getCurrentUser();
    if (!user) return;

    markAllToastsAsRead();

    const {error} = await SupabaseInstance.supabase
      .from('notification')
      .update({
        status: 'read',
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)
      .eq('status', 'unread');

    if (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read', defaultToastOptions);
      return;
    }

    refetchAll();
  }, [markAllToastsAsRead, refetchAll]);

  const markAllAsUnread = useCallback(async () => {
    const user = await SupabaseInstance.getCurrentUser();
    if (!user) return;

    const {error} = await SupabaseInstance.supabase
      .from('notification')
      .update({
        status: 'unread',
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id)
      .eq('status', 'read');

    if (error) {
      console.error('Error marking all notifications as unread:', error);
      toast.error('Failed to mark all notifications as unread', defaultToastOptions);
      return;
    }

    refetchAll();
  }, [refetchAll]);

  const archiveNotification = useCallback(
    async (notificationId: string) => {
      const {error} = await SupabaseInstance.supabase
        .from('notification')
        .update({status: 'archived', updated_at: new Date().toISOString()})
        .eq('notification_id', notificationId);

      if (error) {
        console.error('Error archiving notification:', error);
        toast.error('Failed to archive notification', defaultToastOptions);
        return;
      }

      refetchAll();
    },
    [refetchAll]
  );

  const unarchiveNotification = useCallback(
    async (notificationId: string) => {
      const {error} = await SupabaseInstance.supabase
        .from('notification')
        .update({status: 'read', updated_at: new Date().toISOString()})
        .eq('notification_id', notificationId);

      if (error) {
        console.error('Error un-archiving notification:', error);
        toast.error('Failed to unarchive notification', defaultToastOptions);
        return;
      }

      refetchAll();
    },
    [refetchAll]
  );

  const archiveAllNotifications = useCallback(async () => {
    const user = await SupabaseInstance.getCurrentUser();
    if (!user) return;

    const {error} = await SupabaseInstance.supabase
      .from('notification')
      .update({status: 'archived', updated_at: new Date().toISOString()})
      .eq('user_id', user.id);

    if (error) {
      console.error('Error archiving all notifications:', error);
      toast.error('Failed to archive all notifications', defaultToastOptions);
      return;
    }

    refetchAll();
  }, [refetchAll]);

  const unarchiveAllNotifications = useCallback(async () => {
    const user = await SupabaseInstance.getCurrentUser();
    if (!user) return;

    const {error} = await SupabaseInstance.supabase
      .from('notification')
      .update({status: 'read', updated_at: new Date().toISOString()})
      .eq('user_id', user.id)
      .eq('status', 'archived');

    if (error) {
      console.error('Error un-archiving all notifications:', error);
      toast.error('Failed to unarchive all notifications', defaultToastOptions);
      return;
    }

    refetchAll();
  }, [refetchAll]);

  const removeNotification = useCallback(
    async (notificationId: string) => {
      const {error} = await SupabaseInstance.supabase
        .from('notification')
        .delete()
        .eq('notification_id', notificationId);

      if (error) {
        console.error('Error removing notification:', error);
        return;
      }

      refetchAll();
    },
    [refetchAll]
  );

  // Create a secure channel name using a one-time token
  const getSecureChannelName = useCallback(async () => {
    try {
      const nonce = crypto.randomUUID();
      const {data: channelName, error} = await SupabaseInstance.supabase.rpc('get_notification_channel_token', {
        nonce,
      });

      if (error) throw error;
      if (!channelName) throw new Error('Failed to get channel name');

      return channelName;
    } catch (error) {
      console.error('[Notifications] Error getting secure channel name:', error);
      throw error;
    }
  }, []);

  const setupChannel = useCallback(async () => {
    if (channelInitializingRef.current || !isAuthenticated) return;
    channelInitializingRef.current = true;

    try {
      setConnectionState('connecting');
      const user = await SupabaseInstance.getCurrentUser();

      if (channelRef.current) {
        await channelRef.current.unsubscribe();
        channelRef.current = null;
      }

      const channelName = await getSecureChannelName();

      const newChannel = SupabaseInstance.supabase
        .channel(channelName)
        .on(
          'postgres_changes' as const,
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notification',
            filter: `user_id=eq.${user.id}`,
          },
          onNotificationInsert
        )
        .on(
          'postgres_changes' as const,
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notification',
            filter: `user_id=eq.${user.id}`,
          },
          () => refetchAll()
        )
        .on(
          'postgres_changes' as const,
          {
            event: 'DELETE',
            schema: 'public',
            table: 'notification',
            filter: `user_id=eq.${user.id}`,
          },
          () => refetchAll()
        );

      try {
        newChannel.subscribe();
        channelRef.current = newChannel;
        setConnectionState('connected');
      } catch (err) {
        console.error('[Notifications] Subscription error:', err);
        setConnectionState('error');
      } finally {
        channelInitializingRef.current = false;
      }
    } catch (err) {
      console.error('[Notifications] Setup error:', err);
      setConnectionState('error');
      channelInitializingRef.current = false;
    }
  }, [getSecureChannelName, isAuthenticated, onNotificationInsert, refetchAll]);

  const resetConnection = useCallback(async () => {
    try {
      setConnectionState('disconnected');
      if (channelRef.current) {
        await channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      await setupChannel();
    } catch (error) {
      console.error('[Notifications] Reset error:', error);
      setConnectionState('error');
      throw error;
    }
  }, [setupChannel]);

  // **************************************************************
  // Initialization
  // **************************************************************

  // Subscribe to notification changes
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!mounted || channelRef.current) return;
      await setupChannel();
    };

    init();

    return () => {
      mounted = false;
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [setupChannel]);

  // Create context value
  const contextValue: NotificationsContextT = useMemo(
    () => ({
      notifications,
      archivedNotifications,
      unreadCount,
      loading: isLoadingActive || isLoadingArchived,
      connectionState,
      pagination: {
        currentPage: activeCurrentPage,
        totalPages: activeTotalPages,
        totalItems: activeTotalItems,
        setPage: setActivePage,
      },
      archivedPagination: {
        currentPage: archivedCurrentPage,
        totalPages: archivedTotalPages,
        totalItems: archivedTotalItems,
        setPage: setArchivedPage,
      },
      searchQuery,
      setSearchQuery,
      searchResults,
      isSearching,
      markAsRead,
      markAllAsRead,
      markAllAsUnread,
      archiveNotification,
      unarchiveNotification,
      archiveAllNotifications,
      unarchiveAllNotifications,
      removeNotification,
      createNotification,
      resetConnection,
      searchNotifications,
      toast: toastFn,
      debugEvents: debugEventsRef.current,
    }),
    [
      notifications,
      archivedNotifications,
      unreadCount,
      isLoadingActive,
      isLoadingArchived,
      connectionState,
      activeCurrentPage,
      activeTotalPages,
      activeTotalItems,
      setActivePage,
      archivedCurrentPage,
      archivedTotalPages,
      archivedTotalItems,
      setArchivedPage,
      searchQuery,
      setSearchQuery,
      searchResults,
      isSearching,
      markAsRead,
      markAllAsRead,
      markAllAsUnread,
      archiveNotification,
      unarchiveNotification,
      archiveAllNotifications,
      unarchiveAllNotifications,
      removeNotification,
      createNotification,
      resetConnection,
      searchNotifications,
      toastFn,
    ]
  );

  return <NotificationsContext.Provider value={contextValue}>{children}</NotificationsContext.Provider>;
};

// Create the hook
export function useNotifications(): NotificationsContextT {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
